import { useState } from "react"

const ARRAY_DELIMITER = "|"

export const RailsArray = Symbol("RailsArray")

const DEFAULT_OPTIONS = {
  type: String,
  defaultValue: "",
}

// The `type` value of the config object is a function that takes a string (the query param value)
// and converts it into the value stored in state. The native String and Number functions work
// and there is built in support for an Array of strings to this hook. However, other functions
// may work as well but have not been tested.
export const useQueryParamState = (paramName, config = {}) => {
  const options = normalizeOptions(config)

  const searchParams = new URL(location).searchParams
  const queryParm =
    options.type === RailsArray
      ? searchParams.getAll(`${paramName}[]`)
      : searchParams.get(paramName)
  const value = queryParamToValue(queryParm, options)

  const [stateValue, setStateValue] = useState(value ?? options.defaultValue)

  updateUrl(paramName, valueToQueryParam(stateValue, options), options.type)

  const setter = (newValue) => {
    const queryParamValue = valueToQueryParam(newValue, options)
    updateUrl(paramName, queryParamValue, options.type)
    setStateValue(newValue)
  }

  return [stateValue, setter]
}

function normalizeOptions(options) {
  return Object.assign({}, DEFAULT_OPTIONS, options)
}

function queryParamToValue(
  queryParam,
  { type, defaultValue, arrayItemType = String }
) {
  if (queryParam?.length == null) return null

  switch (type) {
    case Array:
      return queryParam.length
        ? queryParam.split(ARRAY_DELIMITER).map(arrayItemType)
        : defaultValue
    case RailsArray:
      return queryParam.length > 0
        ? queryParam.map(arrayItemType)
        : defaultValue
    default:
      return queryParam.length ? type(queryParam) : defaultValue
  }
}

function valueToQueryParam(value, { type, defaultValue }) {
  switch (type) {
    case Array:
      return (value ?? defaultValue).join(ARRAY_DELIMITER)
    case RailsArray:
      return value ?? defaultValue
    default:
      return value ? type(value) : defaultValue
  }
}

function updateUrl(paramName, queryParam, type) {
  const url = new URL(location)
  const searchParams = url.searchParams

  if (type === RailsArray) {
    searchParams.delete(`${paramName}[]`)

    if (Array.isArray(queryParam) && queryParam.length > 0) {
      queryParam.forEach((value) => {
        searchParams.append(`${paramName}[]`, value)
      })
    }
  } else {
    if (queryParam.length === 0) {
      searchParams.delete(paramName)
    } else {
      searchParams.set(paramName, queryParam)
    }
  }

  history.replaceState(null, null, url.toString())
}
